import type {
  MonetateAction,
  MonetateCartLine,
  MonetateCustomStates,
  MonetateCustomVariable,
  MonetateEvent,
  MonetateEventType,
  MonetateExtractedStates,
  MonetateMappedExperience,
  MonetatePurchaseLine,
  OptimizelyAttributesInput,
  P13nPageType,
} from '#types/p13n'
import type { LocationQuery } from 'vue-router'
import { removeUrlHost } from '#core/utils/removeUrlHost'
import { randomUUID as uuid } from 'uncrypto'

const patterns = [
  { pattern: /\/p\//, value: 'PDP' },
  { pattern: /\/c\//, value: 'PLP' },
  { pattern: /\/cart/, value: 'Cart' },
  { pattern: /\/account/, value: 'Account' },
  { pattern: /\/search/, value: 'Search' },
  { pattern: /searchhub/, value: 'SearchHub' },
  { pattern: /^\/[a-z]{2}-[a-z]{2}$/, value: 'Home' },
  { pattern: /\/order-confirmation/, value: 'Order Confirmation' },
  { pattern: /\/checkout/, value: 'Checkout' }
].map(({ pattern, value }) => ({
  pattern: new RegExp(pattern),
  value
}))

const getPagePattern = (path: string) => {
  for (const { pattern, value } of patterns) {
    if (pattern.test(path))
      return <P13nPageType>value
  }

  return 'Other'
}

/**
 * Extracts the page type from a given path using a set of predefined patterns.
 * @param path The path from which to extract the page type.
 * @category Utils
 * @returns The extracted page type, or 'Other' if no match is found.
 */
export const extractPageType = (path: string) => {
  if (document.querySelector('meta[name="description"]')?.getAttribute('content')?.includes('Page is not available'))
    return 'Error'

  return getPagePattern(path)
}

/**
 * Extracts categories from a given path and optionally adds additional categories.
 * @param path The path from which to extract the categories.
 * @param additionalCategories Additional categories to include (optional).
 * @returns An array of extracted categories.
 * @category Utils
 */
export const extractCategories = (path: string, additionalCategories?: string[]) => {
  const categories: string[] = []

  if (path.match(/\/c\//))
    categories.push(...path.split('/').slice(3).map((str) => str.replace(/-\d+$/, '')))

  if (additionalCategories?.length)
    categories.push(...additionalCategories)

  return categories
}

export const getProductIdFromPath = (path: string, query?) => {
  return `${path.match(/-([A-Z]{2}[A-Z\d]{6,})$/)?.[1]}${query?.color || ''}`
}

export const getCategoryIdFromPLPPath = (path: string) => {
  return path.match(/(\d{4,7})(?=\?|$)/)?.[1]
}

/**
 * Maps an item to either a MonetateCartLine or MonetatePurchaseLine object.
 * @param item The item to map.
 * @returns A MonetateCartLine or MonetatePurchaseLine object.
 * @category Utils
 */
export const mapItemLine = (item, productTypeId): MonetateCartLine | MonetatePurchaseLine => ({
  currency: item.price.currency,
  pid: productTypeId === 'styleId'
    ? item.masterId2
    : item.masterId,
  quantity: item.qty,
  sku: item.slug,
  value: item.price.rowTotal.toFixed(2)
})

/**
 * Extracts additional Monetate events based on the provided event content, event type, and Monetate environment.
 * @param eventContent The content of the event.
 * @param eventType The type of the Monetate event.
 * @param monetateEnv The Monetate environment.
 * @returns An array of additional Monetate events.
 * @category Utils
 */
export const extractExtraEvents = (
  eventContent,
  eventType: MonetateEventType,
  monetateEnv
) => {
  const additionalEvents: MonetateEvent[] = []

  if (eventType === 'monetate:context:Purchase' && eventContent?.items) {
    additionalEvents.push({
      eventType,
      account: eventContent.consumerId,
      domain: window.location.host,
      instance: monetateEnv,
      purchaseId: eventContent.orderNumber,
      purchaseLines: eventContent.items.map((item) => mapItemLine(item, window.vfaConfig?.productIdType))
    })
  }

  if (eventType === 'monetate:context:ProductThumbnailView' && eventContent?.length) {
    const productIds = window.vfaConfig?.productIdType === 'styleId'
      ? eventContent.map((product) => product.styleId)
      : eventContent.map((product) => product.id)
    additionalEvents.push({
      eventType,
      products: productIds
    })
  }

  if (eventType === 'monetate:context:ProductDetailView' && eventContent?.id) {
    additionalEvents.push({
      eventType,
      products: [
        {
          productId: window.vfaConfig?.productIdType === 'styleId'
            ? eventContent.masterId
            : eventContent.id
        }
      ]
    })
  }

  if (eventType === 'monetate:context:Cart' && eventContent?.length) {
    additionalEvents.push({
      eventType,
      cartLines: eventContent.map((item) => mapItemLine(item, window.vfaConfig?.productIdType))
    })
  }

  return additionalEvents
}

/**
 * Extracts custom states from Monetate custom states object.
 * @param customStates The Monetate custom states object.
 * @returns An object containing extracted custom variables and state names.
 * @category Utils
 */
const extractCustomStates = (customStates: MonetateCustomStates): MonetateExtractedStates => {
  const extractedCustomStates: MonetateExtractedStates = {
    customVars: [],
    customStatesNames: []
  }

  for (const [key, value] of Object.entries(customStates)) {
    if (value !== undefined) {
      const stringedValue = value?.toString() || ''
      extractedCustomStates.customVars.push({
        variable: key,
        value: stringedValue
      })
    }

    extractedCustomStates.customStatesNames.push(`${value ? '' : 'not-'}${key}`)
  }

  return extractedCustomStates
}

/**
 * Extracts base Monetate events based on provided parameters such as page details, screen size, language, user agent, etc.
 * @param to An object containing page details like fullPath, path, and query.
 * @param to.fullPath The fullPath attribute on the to object.
 * @param to.path The path attribute on the 'to' object.
 * @param to.query The query attribute on the 'to' object.
 * @param options The height, width, coordinates, customStates, interests, and cart options
 * @param options.height The height of the screen.
 * @param options.width The width of the screen.
 * @param options.coordinates The coordinates of the user.
 * @param options.customStates The custom states object.
 * @param options.interests The interests of the user.
 * @param options.cart The cart details.
 * @param options.stealth If the user is in a stealth group.
 * @returns An array of base Monetate events.
 * @category Utils
 */
export const extractBaseEvents = (
  to: {
    fullPath: string
    path: string
    query: LocationQuery
  },
  { height, width, coordinates, customStates, interests, cart, stealth }
) => {
  const pageType = extractPageType(to.path) || ''
  const customVars: MonetateCustomVariable[] = []
  const customStatesNames: string[] = []

  if (customStates) {
    const extractedCustomState = extractCustomStates(customStates)
    customVars.push(...extractedCustomState.customVars)
    customStatesNames.push(...extractedCustomState.customStatesNames)
  }

  const baseEvents: MonetateEvent[] = [
    {
      eventType: 'monetate:context:PageView',
      url: window.location.href,
      pageType,
      breadcrumbs: extractCategories(to.fullPath),
      categories: extractCategories(to.fullPath, customStatesNames)
    },
    {
      requestId: uuid(),
      eventType: 'monetate:decision:DecisionRequest',
      includeReporting: true
    },
    {
      eventType: 'monetate:context:ScreenSize',
      height,
      width
    },
    {
      eventType: 'monetate:context:Language',
      language: to.path.split('/')[1]
    },
    {
      eventType: 'monetate:context:UserAgent',
      userAgent: window?.navigator?.userAgent
    }
  ]

  if ((window.location.host.includes('preprod')
    || window.location.host.includes('www3')) && stealth !== 'trackingGroup') {
    baseEvents.push({
      eventType: 'monetate:context:IpAddress',
      ipAddress: '123.123.123.123'
    })
  }

  if (pageType === 'PDP') {
    baseEvents.push({
      eventType: 'monetate:context:ProductDetailView',
      products: [
        {
          productId: window.vfaConfig?.productIdType === 'styleId'
            ? getProductIdFromPath(to.path)
            : getProductIdFromPath(to.path, to.query)
        }
      ]
    })
  }

  if (document?.referrer) {
    baseEvents.push({
      eventType: 'monetate:context:Referrer',
      referrer: document.referrer
    })
  }

  if (coordinates) {
    const [latitude, longitude] = coordinates.split(',')
    baseEvents.push({
      eventType: 'monetate:context:Coordinates',
      latitude,
      longitude
    })
  }

  if (pageType === 'Search') {
    baseEvents.push({
      eventType: 'monetate:context:SearchView',
      searchTerm: to.query?.q as string,
      searchTermType: 'text',
      searchType: 'site'
    })
  }

  if (cart?.items?.length) {
    baseEvents.push({
      eventType: 'monetate:context:Cart',
      cartLines: cart.items
    })
  }

  if (interests?.length) {
    customVars.push(
      {
        variable: 'userInterest',
        value: interests.filter(Boolean)
      }
    )
  }

  if (cart?.cartItemTotal) {
    customVars.push({
      variable: 'total-cart',
      value: cart?.cartItemTotal
    })
  }

  if (cart?.discount) {
    customVars.push({
      variable: 'sub-total',
      value: cart?.totals?.itemTotal - cart?.totals?.totalDiscount
    })
  }

  if (customVars?.length) {
    baseEvents.push({
      eventType: 'monetate:context:CustomVariables',
      customVariables: customVars
    })
  }

  return baseEvents
}

/**
 * Gets the variant based on the provided label.
 * @param label The label used to determine the variant.
 * @returns The variant corresponding to the label.
 * @category Utils
 */
export const getVariant = (label: string): string => {
  // This is meant to account for the 2 edge cases of Baseline and Experiment and to evaluate B-Holdout as B
  switch (true) {
    case (label.includes('Experiment')):
      return 'A'
    case (label.includes('Baseline')):
      return 'Baseline'
    case (label[1] === '-'):
      return label[0]
    default:
      return label
  }
}

/**
 * Reduces an array of Monetate actions into a map with experience IDs as keys and corresponding variant and items as values.
 * @param actions An array of Monetate actions to reduce.
 * @returns A map with experience IDs as keys and variant and items as values.
 * @category Utils
 */
export const reduceActions = (actions: MonetateAction[]) => {
  return actions.reduce<Map<string, MonetateMappedExperience>>((vfaMap, action: MonetateAction) => {
    const {
      experience_id,
      experience_name,
      experience_label,
      variant_label
    } = action.impressionReporting?.[0] ?? {}

    // Experiment is converted to A, Control is preserved while in the other cases we use the first letter representing the experiment to avoid cases where variant label contain holdout
    const variant = getVariant(variant_label)
    const experienceId = experience_id.toString()
    const experienceName = experience_name.toString()
    const experienceLabel = experience_label.toString()

    vfaMap.set(experienceId, {
      // for context behind the letter "A" visit https://digital.vfc.com/stash/projects/VEF/repos/canvas-storefront/pull-requests/2641/overview?commentId=193594
      variant,
      id: experienceId,
      name: experienceName,
      label: experienceLabel,
      actionId: action.actionId,
      component: action.component,
      items: action.items
    })
    return vfaMap
  }, new Map())
}

/**
 * Gets the env based on the monetate channel.
 * @param channel The monetate channel.
 * @returns PROD or SANDBOX.
 * @category Utils
 */
export const getEnvFromChannel = (channel) => {
  const parts = channel.split('/')
  return parts[1] === 'p' ? 'PROD' : 'SANDBOX'
}

export const getBloomReachUrl = (path: string, url: string, ids: Record<P13nPageType, string>) => {
  return `${url}${ids[extractPageType(path)] || ids.Other}`
}

export const getBloomReachParams = (siteId: string, path: string, { cart, query, endpointIds }) => {
  let size = 16
  const itemIds = {}
  const pageType = endpointIds[extractPageType(path)] ? extractPageType(path) : 'Other'

  if (pageType === 'Cart') {
    cart.items?.forEach((item, index) => {
      const pid = item.productTypeId === 'styleId'
        ? item.masterId2
        : item.masterId
      return itemIds[pid] = index + 1
    })
  }

  if (pageType === 'PDP') {
    const productId = getProductIdFromPath(path, query)
    itemIds[productId] = 1
  }

  if (pageType === 'SearchHub')
    size = 5

  return {
    siteId,
    language: location.pathname.split('/')[1].split('-')[0],
    size,
    itemIds: itemIds || null,
    ...(pageType === 'PLP' && { categoryIds: getCategoryIdFromPLPPath(path) }),
    ...(pageType === 'Other' && { pagePath: path })
  }
}

export const extractBloomReachProduct = ({
  highCurrent,
  highOriginal,
  historicBestPrice,
  image,
  images, // added to allow search products
  lowCurrent,
  lowOriginal,
  name, // added to allow search products
  percentagePriceDiscount,
  previousBestSalesPrice,
  price, // added to allow search products
  productId,
  recommendationId,
  recommendationName,
  review_average,
  reviews_count,
  title,
  url,
}, showRatings) => {
  return {
    id: productId,
    image: {
      src: image || images, // fallback for search products
      alt: title,
      type: 'image'
    },
    price: {
      highCurrent: Number(highCurrent || price.highCurrent), // fallback for searchhub products
      lowCurrent: Number(lowCurrent || price.lowCurrent),
      lowOriginal: Number(lowOriginal || price.lowOriginal),
      highOriginal: Number(highOriginal || price.highOriginal),
      historicBestPrice: Number(historicBestPrice || price?.historicBestPrice),
      percentagePriceDiscount: Number(percentagePriceDiscount || price?.percentagePriceDiscount),
      previousBestSalesPrice: Number(previousBestSalesPrice || price?.previousBestSalesPrice)
    },
    recommendationId,
    recommendationName,
    title: title || name,
    url: removeUrlHost(url),
    ...(showRatings && {
      rating: {
        showScore: review_average !== undefined,
        score: Number.parseFloat(review_average || '0'),
        ...reviews_count && { reviewsCount: reviews_count }
      }
    }),
  }
}

const concatenateUserStatuses = (userStore) => {
  const { isLoyaltyMember, isWranxProfile, isIpaProfile, isWranxOrIpaProfile, isAthleteProfile } = userStore
  const userStatuses = { isLoyaltyMember, isWranxProfile, isIpaProfile, isWranxOrIpaProfile, isAthleteProfile }
  return Object.entries(userStatuses)
    .filter(([_, v]) => v)
    .map(([k]) => k.replace(/^is/, '').replace(/(Member|Profile)$/, ''))
    .join('|')
}

export const extractCartProducts = (cart, products, attribute: string) => {
  return cart.items.filter(
    (item) => products.map(({ productId }) => productId).includes(item.productId)
  ).map((p) => p[attribute])
}

export const getOptimizelyAttributes = ({
  auth,
  cart,
  landingPage,
  locationData,
  productData,
  route,
  userStore,
  visitNumber,
  windowSize,
}: OptimizelyAttributesInput) => {
  const pageType = getPagePattern(route.path)

  return {
    pageType,
    url: route.fullPath,
    loggedIn: auth.loggedIn || false,
    screenSizeHeight: windowSize.height,
    screenSizeWidth: windowSize.width,
    landingPage: landingPage || false,
    ...(pageType === 'PLP' && { categoryIds: getCategoryIdFromPLPPath(route.path) }),
    ...(pageType === 'PDP' && { productId: getProductIdFromPath(route.path, route.query) }),
    ...(pageType === 'PDP' && { onSale: !!productData?.onSale }),
    ...(pageType === 'PDP' && { isCustoms: !!productData?.isCustoms }),
    ...(pageType === 'Search' && { searchQuery: route.query.q }),
    ...(cart?.totalItems > 0 && { cartTotalItems: cart.totalItems }),
    ...(cart.totals?.total > 0 && { cartTotalAmount: cart.totals.total }),
    ...(cart.totals?.discount > 0 && { cartTotalDiscount: cart.totals.discount }),
    ...(cart.items?.length > 0 && { cartItemsMasterId: cart.items.map((item) => item.masterId).join('|') }),
    ...(cart.items?.length > 0 && { cartItemsProductId: cart.items.map((item) => item.productId).join('|') }),
    ...(auth.loggedIn && { userSegment: concatenateUserStatuses(userStore) }),
    ...(auth.loggedIn && { userInterests: userStore.interests.map((interest) => interest.name).join('|') }),
    ...(visitNumber && { visitNumber: Number(visitNumber) }),
    ...(locationData.latLong?.value && { latitude: Number(locationData.latLong.value.split(',')[0]), longitude: Number(locationData.latLong.value.split(',')[1]) }),
    ...(locationData.country?.value && { country: locationData.country.value }),
    ...(locationData.state?.value && { state: locationData.state.value }),
    ...(locationData.city?.value && { city: locationData.city.value }),
    ...(locationData.zip?.value && { zip: locationData.zip.value })
  }
}

// Across brands there is always a style id represented by the first 8 characters of the product id
const getStyleFromProductId = (productId: string) => {
  return productId.slice(0, 8)
}

const getProductIdFromPLPList = (product) => {
  const isStyle = PRODUCT_STYLE_ID_REGEX.test(product.id)
  if (!isStyle) return product.id
  return getDefaultColorOption(product.attributes, product.id).id
}

export const mapProperties = (eventKey: string, properties) => {
  switch (eventKey) {
    case 'quick_shop': {
      const { product } = properties
      return {
        product,
        style: getStyleFromProductId(product)
      }
    }
    case 'order_summary': {
      const { total, items } = properties
      return {
        total,
        products: items.map(({ masterId }) => masterId).join(' - '),
        styles: items.map(({ masterId }) => getStyleFromProductId(masterId)).join(' - '),
        SKUS: items.map(({ productId }) => productId).join(' - ')
      }
    }
    case 'order_confirmation': {
      const { total, items } = properties
      return {
        total,
        products: items.map(({ masterId }) => masterId).join(' - '),
        styles: items.map(({ masterId }) => getStyleFromProductId(masterId)).join(' - '),
        SKUS: items.map(({ productId }) => productId).join(' - ')
      }
    }

    case 'product_add': {
      const { total, items, itemsToAdd } = properties
      return {
        total,
        product: extractCartProducts(items, itemsToAdd, 'masterId').join(' | '),
        style: extractCartProducts(items, itemsToAdd, 'masterId').map((id) => getStyleFromProductId(id)).join(' | '),
        SKU: extractCartProducts(items, itemsToAdd, 'productId').join(' | ')
      }
    }

    case 'product_remove': {
      const { total, product } = properties
      return {
        total,
        product: product?.masterId,
        style: getStyleFromProductId(product?.masterId),
        SKU: product?.productId
      }
    }

    case 'category_view': {
      const { products, categoryName, categoryID, categoryBreadcrumbs, productsPerPage } = properties
      return {
        products: products.slice(0, productsPerPage).map((product) => getProductIdFromPLPList(product)).join(' - '),
        styles: products.slice(0, productsPerPage).map((product) => getStyleFromProductId(product.id)).join(' - '),
        categoryName,
        categoryID,
        categoryBreadcrumbs
      }
    }

    case 'product_view': {
      const { product } = properties
      return {
        product: product.id,
        productName: product.name,
        style: product.id.slice(0, 8)
      }
    }

    default:
      return properties
  }
}
